import React from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { toggleFolderExpandedAtom } from '../../../../atoms/v3/projectV2Atoms';
import { Folder } from '../../../../types/folder';

interface FolderDividerProps {
  folder: Folder | null; // null 表示"未分组"
  projectCount: number;
  isExpanded: boolean;
  className?: string;
  onFolderClick?: (folderId: string) => void; // 文件夹名称点击回调
}

/**
 * 文件夹分割线组件
 * 显示文件夹名称、项目数量，支持折叠/展开功能
 * 参考 ProjectDivider.tsx 的设计
 */
export const FolderDivider: React.FC<FolderDividerProps> = ({
  folder,
  projectCount,
  isExpanded,
  className = '',
  onFolderClick
}) => {
  const { t } = useTranslation(['v3/projects']);
  const [isDark] = useAtom(isDarkAtom);
  const [, toggleFolderExpanded] = useAtom(toggleFolderExpandedAtom);

  const handleToggle = () => {
    // 对于真实文件夹使用文件夹ID，对于未分组使用特殊ID 'ungrouped'
    const folderId = folder ? folder.id : 'ungrouped';
    toggleFolderExpanded(folderId);
  };

  const handleFolderClick = () => {
    // 对于真实文件夹使用文件夹ID，对于未分组使用特殊ID 'ungrouped'
    const folderId = folder ? folder.id : 'ungrouped';
    onFolderClick?.(folderId);
  };

  // 获取显示名称
  const getDisplayName = () => {
    if (!folder) {
      return t('v3/projects:folders.ungrouped', '未分组');
    }
    return folder.name;
  };

  // 获取图标
  const getIcon = () => {
    if (!folder) {
      return '📋'; // 未分组使用剪贴板图标
    }
    return '📁'; // 文件夹图标
  };

  // 获取文件夹ID用于滚动定位
  const folderId = folder ? folder.id : 'ungrouped';

  return (
    <div
      id={`folder-${folderId}`}
      className={`flex items-center justify-between py-2 px-4 ${className}`}
    >
      {/* 左侧：图标、名称、数量 */}
      <div className="flex items-center gap-2 flex-1">
        {/* 展开/折叠箭头按钮 */}
        <button
          onClick={handleToggle}
          className={`
            p-1 rounded transition-colors
            ${isDark
              ? 'text-ios-dark-gray2 hover:text-ios-dark-gray1 hover:bg-ios-dark-gray4'
              : 'text-ios-gray2 hover:text-ios-gray1 hover:bg-ios-gray5'
            }
          `}
        >
          <svg
            className={`
              w-4 h-4 transition-transform duration-200
              ${isExpanded ? 'rotate-90' : 'rotate-0'}
            `}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* 文件夹信息（可点击滚动） */}
        <div
          onClick={handleFolderClick}
          className={`
            flex items-center gap-2 text-ios-subhead font-medium transition-colors cursor-pointer flex-1
            ${isDark
              ? 'text-ios-dark-gray2 hover:text-ios-dark-gray1'
              : 'text-ios-gray2 hover:text-ios-gray1'
            }
          `}
        >
          {/* 图标 */}
          <span className="text-base">{getIcon()}</span>

          {/* 文件夹名称 */}
          <span className="truncate">{getDisplayName()}</span>

          {/* 项目数量 */}
          <span className={`
            text-ios-caption px-1.5 py-0.5 rounded-full
            ${isDark
              ? 'bg-ios-dark-gray4 text-ios-dark-gray2'
              : 'bg-ios-gray5 text-ios-gray2'
            }
          `}>
            {projectCount}
          </span>
        </div>
      </div>

      {/* 右侧：分割线 */}
      <div className={`
        flex-1 ml-4 h-px
        ${isDark ? 'bg-ios-dark-gray4' : 'bg-ios-gray5'}
      `} />
    </div>
  );
};
